import React, { useState, useEffect, lazy, Suspense } from "react";
import axios from "../api/axios";
const LazyRecaptcha = lazy(() => import("./Recaptcha"));

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        contactMethod: 'email', // default selection
        email: '',
        phone: '',
        message: ''
    });

    const [recaptchaToken, setRecaptchaToken] = useState(null); // ✅ Store reCAPTCHA token
    const [submitted, setSubmitted] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false); // ✅ Track form completion

    useEffect(() => {
        // ✅ Check if all required fields are filled
        const { name, contactMethod, email, phone, message } = formData;
        if (name && message && (contactMethod === "email" ? email : phone)) {
            setIsFormComplete(true);
        } else {
            setIsFormComplete(false);
        }
    }, [formData]);

    const handleChange = (e) => {
        let { name, value } = e.target;
    
        // ✅ Auto-format phone input as (XXX-XXX-XXXX) in real-time
        if (name === "phone") {
            value = value.replace(/\D/g, ""); // Remove non-numeric characters
    
            if (value.length > 3 && value.length <= 6) {
                value = `${value.slice(0, 3)}-${value.slice(3)}`;
            } else if (value.length > 6) {
                value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
            }
    
            // ✅ Ensures no more than 12 characters (XXX-XXX-XXXX)
            value = value.slice(0, 12);
        }
    
        setFormData({ ...formData, [name]: value });
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!recaptchaToken) {
            alert("❌ Please complete the reCAPTCHA.");
            return;
        }

        try {
            // ✅ Determine the contact value (either email or phone)
            const contact = formData.contactMethod === "email" ? formData.email : formData.phone;

            const payload = {
                name: formData.name,
                contactMethod: formData.contactMethod,
                contact,
                message: formData.message,
                recaptchaToken // ✅ Send token to backend
            };

            await axios.post("/api/public/messages", payload, { withCredentials: true });

            setSubmitted(true);
            setRecaptchaToken(null); // ✅ Reset token after submission

            // ✅ Reset form after submission
            setFormData({
                name: '',
                contactMethod: 'email',
                email: '',
                phone: '',
                message: ''
            });

        } catch (error) {
            console.error("🔥 Error submitting message:", error.response?.data || error.message);
            alert("❌ Failed to send message. Please try again.");
        }
    };

    useEffect(() => {
        if (submitted) {
            const timer = setTimeout(() => {
                setSubmitted(false);
            }, 5000); // ✅ Hide message after 5 seconds
    
            return () => clearTimeout(timer); // ✅ Cleanup timeout on unmount
        }
    }, [submitted]);

    return (
        <div className="contact-form">
            <h2>Send Us a Message</h2>
            <form onSubmit={handleSubmit}>
            {submitted && <p className="success-message">✅ Message sent successfully!</p>}
                <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        maxLength="50" // ✅ Limit name to 50 characters
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="contactMethod">Preferred Contact Method</label>
                    <select
                        id="contactMethod"
                        name="contactMethod"
                        value={formData.contactMethod}
                        onChange={handleChange}
                        required
                    >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                    </select>
                </div>

                {formData.contactMethod === "email" ? (
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            maxLength="254" // ✅ Limit email length per RFC standards
                            required
                        />
                    </div>
                ) : (
                    <div className="form-group">
                        <label htmlFor="phone">Phone</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="XXX-XXX-XXXX"
                            maxLength="12" // ✅ Ensures correct length
                            required
                        />
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength="2000" // ✅ Prevents excessively long messages
                        required
                    ></textarea>
                </div>

                {/* ✅ Render reCAPTCHA only when all fields are filled */}
                {isFormComplete && (
                    <Suspense fallback={<p>Loading reCAPTCHA...</p>}>
                        <LazyRecaptcha setToken={setRecaptchaToken} />
                    </Suspense>
                )}


                <button type="submit" className="submit-btn" disabled={!recaptchaToken}>Send Message</button>
            </form>
        </div>
    );
};

export default ContactForm;
